import { useEffect, useState } from "react";
import { account, mailSent, serverFeatures } from "../account.ts";
import { useStore } from "../hooks.ts";
import { messageOf } from "./Login.tsx";

const LATER_KEY = "lagerlog.confirm-later";
const LATER_MS = 7 * 86_400_000;

function putOff(): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(LATER_KEY) ?? 0) < LATER_MS;
  } catch {
    return false;
  }
}

/**
 * „Bitte bestätige deine E-Mail-Adresse“ (Martin, 25.10.2026): addresses from before count as
 * unconfirmed, and only a confirmed one gets a link for a new password. Shown on an account's
 * device where the app sends mail, with a button for the link; „Später“ hides it for 7 days on
 * this device. „Mein Konto“ shows the state itself.
 */
export function ConfirmBanner({ route }: { route: string }) {
  const { store } = useStore();
  const [waiting, setWaiting] = useState(false);
  const [later, setLater] = useState(putOff);
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  const accountDevice = !!store.config && !store.config.token;

  useEffect(() => {
    if (!accountDevice || later) return;
    let gone = false;
    Promise.all([serverFeatures(), account.me()]).then(
      ([f, me]) => {
        if (!gone) setWaiting(f.mail && !me.demo && !!me.user.email && (!me.user.emailConfirmed || !!me.user.emailPending));
      },
      () => undefined,
    );
    return () => {
      gone = true;
    };
  }, [accountDevice, later]);

  if (!waiting || later || route === "konto") return null;
  async function send() {
    setState({ busy: true });
    try {
      const { email } = await account.emailAgain();
      setState({ ok: mailSent(email) });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  function postpone() {
    try {
      localStorage.setItem(LATER_KEY, String(Date.now()));
    } catch {
      // Without storage it comes back on the next start.
    }
    setLater(true);
  }
  return (
    <div className="banner confirm-banner" data-testid="confirm-banner">
      <span>{state.ok ?? state.error ?? "Bitte bestätige deine E-Mail-Adresse. Dann kannst du dein Passwort selbst zurücksetzen."}</span>
      {!state.ok && (
        <span className="buttons">
          <button disabled={state.busy} onClick={send} data-testid="confirm-send">
            Mail schicken
          </button>
          <button className="link" onClick={postpone} data-testid="confirm-later">
            Später
          </button>
        </span>
      )}
    </div>
  );
}
